import { useMemo, useState } from 'react';

import { Box } from '@mui/material';

import { ChartProvider } from '@/components/Chart/context/ChartContext';
import { CsvDownloadProvider } from '@/components/Chart/context/CsvTableContext';
import ExportChartButton from '@/components/Chart/ExportButton/ExportChartButton';
import type { ChartType } from '@/components/Chart/types/highcharts';
import Loader from '@/components/Loader';
import Tabs from '@/components/Tabs';
import type { FactsheetDetails, FactsheetTab } from '@/features/Factsheet/types';
import useUserDetails from '@/hooks/useUserDetails';
import { LightModeProvider } from '@/pages/Markets/MarketsComponents/LightMode/contexts/LightModeContext';
import type { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';

import AuthenticatedFwdCurveChart from './AuthenticatedFwdCurveChart';
import AuthenticatedSpotChart from './AuthenticatedSpotChart';
import BlurBackdrop from './BlurBackdrop';
import ChartTabContent from './ChartTabContent';
import { createChartSeries } from './createChartSeries';
import UnauthenticatedChart from './UnauthenticatedChart';

const GRAPH_TYPE_MAP: Record<string, ChartType> = { Spot: 'spot', Forward: 'forwardSimple' };

interface ChartTabProps {
  factsheet: FactsheetDetails;
}
const ChartTab = ({ factsheet }: ChartTabProps) => {
  const spotAlias = factsheet.indexDetails['spotAlias'] as string | undefined;
  const { isUserPremium, isUserExpired, status } = useUserDetails();
  const [activeTab, setActiveTab] = useState(0);

  const isLoading = status === 'loading';
  const isAuthorized = status !== undefined && isUserPremium && !isUserExpired;

  const templateSpotSeries = useMemo((): TemplateChartSeries[] => {
    if (!isAuthorized || factsheet.spotSymbols.length === 0) {
      return [];
    }

    const symbol = factsheet.spotSymbols[0];
    return [createChartSeries({ factsheet, symbol, spotAlias, includePeriod: true })];
  }, [factsheet, isAuthorized, spotAlias]);

  const templateForwardSeries = useMemo((): TemplateChartSeries[] => {
    if (!isAuthorized || factsheet.fwdCurveSymbols.length === 0) {
      return [];
    }

    const symbol = factsheet.fwdCurveSymbols[0];
    return [createChartSeries({ factsheet, symbol, spotAlias, includePeriod: false })];
  }, [factsheet, isAuthorized, spotAlias]);

  const contentTabs = useMemo((): FactsheetTab[] => {
    const tabs: FactsheetTab[] = [];

    if (factsheet.spotSymbols.length > 0) {
      tabs.push({
        position: 0,
        name: 'Spot',
        component: <AuthenticatedSpotChart templateChartSeries={templateSpotSeries} />
      });
    }

    if (factsheet.fwdCurveSymbols.length > 0) {
      tabs.push({
        position: 1,
        name: 'Forward',
        component: <AuthenticatedFwdCurveChart templateChartSeries={templateForwardSeries} />
      });
    }

    return tabs;
  }, [
    factsheet.spotSymbols.length,
    factsheet.fwdCurveSymbols.length,
    templateSpotSeries,
    templateForwardSeries
  ]);

  if (isLoading) {
    return <Loader />;
  }

  const activeGraphType = GRAPH_TYPE_MAP[contentTabs[activeTab]?.name] ?? 'spot';

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <BlurBackdrop />
        {!isAuthorized && <UnauthenticatedChart />}
      </Box>

      {isAuthorized && (
        <CsvDownloadProvider>
          <ChartProvider>
            <LightModeProvider>
              <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between' }}>
                {contentTabs.length > 1 && (
                  <Tabs
                    activeTab={activeTab}
                    tabs={contentTabs.map(({ name }) => ({ name }))}
                    onChange={setActiveTab}
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                  <ExportChartButton graphType={activeGraphType} />
                </Box>
              </Box>

              <ChartTabContent
                activeTab={activeTab}
                contentTabs={contentTabs}
                factsheet={factsheet}
              />
            </LightModeProvider>
          </ChartProvider>
        </CsvDownloadProvider>
      )}
    </>
  );
};

export default ChartTab;
